import { array, fn } from '@ember/helper';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { PopupListItem, Service } from 'webapp-types';

import highlightResult from 'webapp/helpers/highlight-result';
import joinClasses from 'webapp/helpers/join-classes';
import visibleCheckItem from 'webapp/modifiers/visible-check-item';
import CommonRecipientsListComponent from 'webapp/components/common/recipients-list';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import DummyCellComponent from 'webapp/components/dummy-cell';
import FlexibleToggleCellComponent from 'webapp/components/flexible-toggle-cell';
import FormCheckboxComponent from 'webapp/components/form/checkbox';
import OptionsButtonComponent from 'webapp/components/options-button';
import TagContainerComponent from 'webapp/components/tag-container';

import type BatchSelect from 'webapp/models-custom/batch-select';
import type ReportDelivery from 'webapp/models-custom/report-delivery';

interface Args {
	model: ReportDelivery;
	batchSelect: BatchSelect<ReportDelivery>;
}

type Action = 'downloadPdf' | 'resend' | 'delete';
type Option = PopupListItem<Action>;

export default class ReportsArchiveTableRowComponent extends Component<Args> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/report-deliveries')
	declare mmReportDeliveriesService: Service.ModelManager.ReportDeliveries;
	@service('request') declare requestService: Service.Request;
	@service('popup') declare popupService: Service.Popup;

	@tracked isVisibleOnScreen = false;
	@tracked showAll = false;

	options: Option[] = [
		{
			icon: 'Pdf',
			text: 'Download PDF',
			action: 'downloadPdf',
		},
		{
			icon: 'Mail',
			text: 'Resend',
			action: 'resend',
		},
		{
			class: 'delete',
			text: 'Delete',
			action: 'delete',
			icon: 'Trash',
		},
	];

	get slideText(): string {
		const count = this.args.model.slideCount;
		return `${count} slide${count !== 1 ? 's' : ''}`;
	}

	get isSelected(): boolean {
		const { batchSelect, model } = this.args;
		return batchSelect.isItemSelected(model);
	}

	get createdByText(): string {
		const createdByName = this.args.model.report?.createdByUser?.name;
		return createdByName ? `by ${createdByName}` : '';
	}

	// get approvedByData(): { name: string; avatar: string; isMe: boolean; } | null {
	// 	const user = this.args.model.approvedByUser;
	// 	let userData;
	// 	if (user) {
	// 		const isMe = user.id === this.currentUserService.id;
	// 		userData = {
	// 			isMe,
	// 			name: isMe ? 'You' : user.name,
	// 			avatar: user.avatar
	// 		};
	// 	}
	// 	return userData || null;
	// }

	@action
	doOptionsAction(actionName: string): void {
		this[actionName as Action]();
	}

	@action
	downloadPdf(): void {
		if (this.currentUserService.handleDemo()) {
			return;
		}

		window.open(this.args.model.reportUrl, '_blank');
	}

	@action
	resend(): void {
		this.mmReportDeliveriesService.resend(this.args.model);
	}

	@action
	delete(): void {
		if (this.currentUserService.handleDemo()) {
			return;
		}

		this.args.batchSelect.unselectAll();
		this.popupService.openConfirmPopup({
			onConfirm: () => {
				void this.mmReportDeliveriesService.delete(this.args.model);
			},
			title: `Are you sure you want to delete the archived PDF '${this.args.model.title}'?`,
			text: '<strong>This cannot be undone.</strong>',
			confirmAction: 'Delete',
			cancelAction: 'Cancel',
		});
	}

	@action
	visibleChanged(isVisible: boolean): void {
		this.isVisibleOnScreen = isVisible;
	}

	@action
	toggleShowAll() {
		this.showAll = !this.showAll;
	}
	<template>
		{{#if this.isVisibleOnScreen}}
			<tr
				class={{joinClasses
					(array
						"table-row" (if this.showAll "all-shown") (if this.isSelected "selected")
					)
				}}
			>
				<td class="cc">
					<FormCheckboxComponent
						@name="report-archive-table-row"
						@selected={{this.isSelected}}
						@theme="blue"
						@onChange={{fn @batchSelect.changeSelection @model}}
					/>
				</td>
				<td class="c0 no-grow first">
					<span class="pdf-icon-wrapper">
						<CommonUiIconComponent @name="Pdf" @size="lg" />
					</span>
				</td>
				<td class="c1">
					<strong>
						{{highlightResult text=@model.title match=@model.activeFilter}}
					</strong>
					<small>{{this.slideText}}</small>
				</td>

				<td class="prio-low" data-label="Recipients: ">
					<CommonRecipientsListComponent @recipients={{@model.mappedRecipients}} />
				</td>

				<td class="c2 prio-high" data-label="Generated: ">
					{{@model.timestampText}}
					<br />
					{{this.createdByText}}
				</td>

				{{!--
        <td class="c4 prio-low" data-label="Approved by: ">
            {{#if this.approvedByData}}
                <div class="owner {{if this.approvedByData.isMe "green"}}">
                    <AvatarOrInitials
                        @name={{this.approvedByData.name}}
                        @avatar={{this.approvedByData.avatar}}
                    />
                    {{this.approvedByData.name}}
                </div>
            {{else}}
                <span class="fade">No approval needed</span>
            {{/if}}
        </td>
        --}}
				<td class="c4 prio-low" data-label="Tags: ">
					{{#if @model.report}}
						<TagContainerComponent
							@model={{@model.report}}
							@canEdit={{@model.report.canCurrentUserEdit}}
						/>
					{{/if}}
				</td>
				<td class="c6 button-ct last">
					<OptionsButtonComponent
						@options={{this.options}}
						@onAction={{this.doOptionsAction}}
					/>
				</td>

				<FlexibleToggleCellComponent
					@showAll={{this.showAll}}
					@toggleShowAll={{this.toggleShowAll}}
				/>
			</tr>

		{{else}}

			<tr class="table-row" {{visibleCheckItem this.visibleChanged}}>
				<DummyCellComponent />
			</tr>

		{{/if}}
	</template>
}
